import React, { useEffect, useMemo, useState } from 'react';

interface ProgressCircleProps {
  progress: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
  animationDuration?: number; // Duration in milliseconds
}

/**
 * ProgressCircle Component
 *
 * A circular progress indicator that displays:
 * - A background circle in light gray
 * - A progress circle in blue with loading animation
 * - Percentage in the center
 *
 * The component is customizable with:
 * - Progress percentage (0-100)
 * - Circle size (default: 96px)
 * - Stroke width (default: 10px)
 * - Animation duration (default: 1000ms)
 * - Additional classes for styling
 */
export default function ProgressCircle({
  progress,
  size = 96,
  strokeWidth = 10,
  className = '',
  animationDuration = 1000,
}: ProgressCircleProps): React.JSX.Element {
  // State for animated progress
  const [animatedProgress, setAnimatedProgress] = useState(0);

  // Memoize calculations
  const { radius, circumference, strokeDashoffset } = useMemo(() => {
    // Use a fixed radius that matches the SVG viewBox (45 units in a 100x100 viewBox)
    const r = 45;
    const c = r * 2 * Math.PI;

    // Ensure strokeDashoffset correctly hides stroke at 0%
    const offset = (1 - animatedProgress / 100) * c;

    return {
      radius: r,
      circumference: c,
      strokeDashoffset: Math.max(0, offset),
    };
  }, [animatedProgress]);

  useEffect(
    () => {
      let startTime: number | null = null;
      let animationFrameId: number;
      const startProgress = animatedProgress;

      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progressRatio = Math.min(elapsed / animationDuration, 1);

        const easedProgress =
          startProgress + (progress - startProgress) * (1 - Math.pow(1 - progressRatio, 3));
        setAnimatedProgress(easedProgress);

        if (progressRatio < 1) {
          animationFrameId = requestAnimationFrame(animate);
        }
      };

      animationFrameId = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrameId);
    },
    // Dependency on `progress` for allowing mid-animation updates
    [progress, animationDuration],
  );

  return (
    <div className={`relative ${className}`} style={{ width: size, height: size }}>
      <svg className='w-full h-full transform -rotate-90' viewBox='0 0 100 100'>
        {/* Background Circle */}
        <circle
          cx='50'
          cy='50'
          r={radius}
          stroke='lightgray'
          strokeWidth={strokeWidth}
          fill='none'
        />
        {/* Progress Circle */}
        <circle
          cx='50'
          cy='50'
          r={radius}
          className='stroke-primary_mid transition-[stroke-dashoffset] duration-300 ease-out'
          strokeWidth={strokeWidth}
          fill='none'
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap='round'
        />
      </svg>
      {/* Percentage in Center */}
      <span
        className={`absolute inset-0 flex items-center justify-center font-semibold text-text_gray_800 ${size < 50 ? 'text-xs' : 'text-sm'}`}
      >
        {Math.round(animatedProgress)}%
      </span>
    </div>
  );
}
